import {useState} from 'react';
import styled from 'styled-components';
import {useDroppable} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {Detail, Icon} from '@forecast-it/design-system';
import type {Ticket, TicketStatus} from '../../types/ticket';
import type {MentionUser} from '../../hooks/useMentionUsers';
import {STATUS_LABELS, TicketStatusBadge} from '../TicketStatusBadge';
import {TicketCard} from './TicketCard';

interface Props {
	status: TicketStatus;
	tickets: Ticket[];
	canDragTicket: (t: Ticket) => boolean;
	showDropPlaceholder?: boolean;
	users?: MentionUser[];
	onAssign?: (ticketId: string, userId: string | null, note?: string) => void;
	onResolve?: (ticketId: string) => void;
	onDelete?: (ticketId: string) => void;
}

// The column is not a surface — no tray, no fill, no border. The cards are the objects
// on the board; a filled column behind them would put a second box around every one.
// Only the drop target is drawn, and only while something is being dragged over it.
const Column = styled.section<{$highlighted: boolean}>`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	padding: ${p => p.theme.sys.spacing[200]};
	transition: background 0.15s;
	width: 100%;
	background: ${p => (p.$highlighted ? p.theme.sys.color.background.disabled['1'] : 'transparent')};

	@media (min-width: 640px) {
		width: calc(50% - 0.5rem);
	}
	@media (min-width: 768px) {
		/* Columns share the width evenly and stop shrinking at a card's minimum, so a
		   narrow window scrolls sideways rather than crushing every card at once. */
		flex: 1 0 230px;
		min-width: 230px;
		width: auto;
	}
`;

const Header = styled.header`
	display: flex;
	cursor: pointer;
	align-items: center;
	justify-content: space-between;
	gap: ${p => p.theme.sys.spacing[200]};
	@media (min-width: 640px) {
		cursor: default;
	}
`;

const HeaderRight = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;

// Only a phone gets the collapse affordance; from tablet up every column is open and
// the chevron would be a control that does nothing.
const Chevron = styled.span<{$open: boolean}>`
	display: inline-flex;
	transition: transform 0.15s;
	transform: rotate(${p => (p.$open ? '180deg' : '0')});
	@media (min-width: 640px) {
		display: none;
	}
`;

const CardList = styled.div<{$collapsed: boolean}>`
	display: ${p => (p.$collapsed ? 'none' : 'flex')};
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
	min-height: 40px;
	@media (min-width: 640px) {
		display: flex;
	}
`;

const DropPlaceholder = styled.div`
	min-height: 132px;
	border-radius: ${p => p.theme.sys.size.borderradius['5']};
	border: ${p => p.theme.sys.size.borderwidth.thick} dashed ${p => p.theme.sys.color.border.selected};
`;

/**
 * One status's worth of the board: a status pill, a count, and the cards under it.
 *
 * The header reuses `TicketStatusBadge`, so a status reads identically here, in the
 * list and on the ticket itself. The count is quiet text rather than a badge of its
 * own — two pills side by side would compete, and the number is a fact about the
 * column, not a label on it.
 */
export function BoardColumn({
	status,
	tickets,
	canDragTicket,
	showDropPlaceholder = false,
	users,
	onAssign,
	onResolve,
	onDelete,
}: Props) {
	const {setNodeRef, isOver} = useDroppable({id: `column:${status}`});
	const ids = tickets.map(t => t.id);
	const [collapsed, setCollapsed] = useState(true);
	const label = STATUS_LABELS[status];

	return (
		<Column ref={setNodeRef} aria-label={label} $highlighted={isOver || showDropPlaceholder}>
			<Header onClick={() => setCollapsed(c => !c)}>
				<TicketStatusBadge status={status} />
				<HeaderRight>
					<Detail color="medium">{String(tickets.length)}</Detail>
					<Chevron $open={!collapsed} aria-hidden="true">
						<Icon icon="caretDown" size="s" newColor="disabled" />
					</Chevron>
				</HeaderRight>
			</Header>

			<SortableContext items={ids} strategy={verticalListSortingStrategy}>
				<CardList $collapsed={collapsed}>
					{tickets.length === 0 && !showDropPlaceholder ? (
						<Detail color="medium">—</Detail>
					) : (
						tickets.map(t => (
							<TicketCard
								key={t.id}
								ticket={t}
								draggable={canDragTicket(t)}
								users={users}
								onAssign={onAssign}
								onResolve={onResolve}
								onDelete={onDelete}
							/>
						))
					)}
					{showDropPlaceholder && <DropPlaceholder aria-label={`Drop ticket in ${label}`} />}
				</CardList>
			</SortableContext>
		</Column>
	);
}
